"use client";

import Link from "next/link";
import { useSyncExternalStore } from "react";
import { ArrowRight, X } from "lucide-react";

/**
 * Full-bleed offer strip above the header. Scrolls away with the page — the
 * header underneath is the thing that sticks. Dismissal is remembered locally;
 * bump the key when the offer changes so the bar comes back.
 */

const STORAGE_KEY = "sc.announcement.free-credits.v1";

/**
 * localStorage as an external store: the bar renders on the server, then swaps
 * to the stored answer at hydration instead of flashing in from an effect.
 * `storage` only fires in other tabs, so dismissal notifies this one directly.
 */
const listeners = new Set<() => void>();

function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  window.addEventListener("storage", onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener("storage", onStoreChange);
  };
}

function isDismissed() {
  return window.localStorage.getItem(STORAGE_KEY) === "1";
}

function dismiss() {
  window.localStorage.setItem(STORAGE_KEY, "1");
  for (const listener of listeners) listener();
}

/**
 * The bar keeps citron-on-ink in both themes, so the colors are literal rather
 * than token-driven: `--ink` inverts in dark mode and would wash out on citron.
 */
export function AnnouncementBar({
  cta = "Find my leads",
  href = "/register",
  message,
}: {
  cta?: string;
  href?: string;
  message: string;
}) {
  const dismissed = useSyncExternalStore(subscribe, isDismissed, () => false);

  if (dismissed) return null;

  return (
    <div className="bg-signal relative text-[#111310]">
      <div className="mx-auto flex max-w-[1240px] items-center justify-center gap-x-2 px-12 py-2.5 text-center">
        <p className="text-[13px] leading-5 text-balance">
          {message}{" "}
          <Link
            href={href}
            className="group ml-1 inline-flex items-center gap-1 font-medium underline decoration-[#111310]/30 underline-offset-[3px] transition-colors hover:decoration-[#111310]"
          >
            {cta}
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </p>
      </div>

      <button
        type="button"
        onClick={dismiss}
        className="absolute right-3 top-1/2 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-full transition-colors hover:bg-[#111310]/10 sm:right-5"
      >
        <span className="sr-only">Dismiss announcement</span>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
